import { useCallback } from 'react';
import { sdk } from '@/sdk';
import { Category } from '@shared/types/product/Category';
import { toLeanCategories } from '@/utils/category-projection';
import useSWR from 'swr';

// CT has ~2k categories today; the BFF tree builder caps at the `limit`
// we pass. 500 is CT's per-request max — proper cursor pagination is the
// long-term fix.
const CATEGORIES_LIMIT = 500;

export const useCategories = () => {
  const { data: categoriesData, mutate: mutateCategories } = useSWR('/action/product/queryCategories?format=tree', () =>
    sdk.composableCommerce.product.queryCategories({ format: 'tree', limit: CATEGORIES_LIMIT }),
  );

  const { data: flatCategoriesData, mutate: mutateFlatCategories } = useSWR(
    '/action/product/queryCategories?format=flat',
    () => sdk.composableCommerce.product.queryCategories({ format: 'flat', limit: CATEGORIES_LIMIT }),
  );

  // EWM-837 (A1) — project to the SAME lean shape the layout seeds. SWR
  // revalidates the seeded key on mount, so without this the components would
  // first render against the trimmed seed and then re-render against a fuller
  // refetch — the exact shape drift that makes a seeded cache untrustworthy.
  const categories = categoriesData?.isError ? [] : toLeanCategories((categoriesData?.data?.items as Category[]) ?? []);
  const flatCategories = flatCategoriesData?.isError
    ? []
    : toLeanCategories((flatCategoriesData?.data?.items as Category[]) ?? []);

  const mutate = useCallback(() => {
    mutateCategories();
    mutateFlatCategories();
  }, [mutateCategories, mutateFlatCategories]);

  return { categories, flatCategories, mutate };
};
